import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from 'react';
import { useGet } from '../hooks';
import { AuditHistory, AuditEntityType } from '@/types/audit';

interface AuditLogsContextState {
  data: AuditHistory | null;
  loading: boolean;
  error: string | null;
  fetchAuditLogs: (
    entityType: AuditEntityType,
    entityId: number,
    page?: number,
    size?: number
  ) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

interface AuditLogsProviderProps {
  children: ReactNode;
  restaurantId: number | null;
}

const AuditLogsContext = createContext<AuditLogsContextState | undefined>(
  undefined
);

export const AuditLogsProvider: React.FC<AuditLogsProviderProps> = ({
  children,
  restaurantId: _restaurantId,
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error, refetch } = useGet<AuditHistory>(
    '',
    {},
    false,
    true
  );

  const fetchAuditLogs = useCallback(
    (entityType: AuditEntityType, entityId: number, page = 0, size = 50) => {
      const url =
        process.env.REACT_APP_API_DOMAIN +
        `audit/${entityType}/${entityId}/audit-history?page=${page}&size=${size}`;
      refetch(url);
      setCurrentPage(page);
    },
    [refetch]
  );

  const contextValue: AuditLogsContextState = {
    data,
    loading,
    error,
    fetchAuditLogs,
    currentPage,
    setCurrentPage,
  };

  return (
    <AuditLogsContext.Provider value={contextValue}>
      {children}
    </AuditLogsContext.Provider>
  );
};

export const useAuditLogs = (): AuditLogsContextState => {
  const context = useContext(AuditLogsContext);
  if (context === undefined) {
    throw new Error('useAuditLogs must be used within an AuditLogsProvider');
  }
  return context;
};

export { AuditLogsContext };
